import { useState } from 'react';
import { useImageAsins } from './useImageAsins';

/**
 * A small product image tile, keyed by child ASIN.
 *
 * The product identity is the child ASIN, so several seller SKUs can share one
 * image; a SKU with no ASIN has no image. The image is served (cached, with an
 * ETag) by GET /api/catalog/images/:asin, which 404s when there is none.
 *
 * To keep a page of hundreds of rows from firing hundreds of authenticated 404s,
 * it first consults the shared set of ASINs that HAVE an image (useImageAsins, one
 * deduped request for the whole page): only an ASIN in that set is rendered as an
 * `<img>`; every other tile is the placeholder, drawn with no network request.
 *
 * The image itself is a plain lazy `<img>` — nothing heavy in the initial bundle.
 * On a load error (a race where the file went missing) or when the ASIN is empty
 * or absent from the set, it falls back to a gray square showing the first one or
 * two letters of the title, or a neutral picture glyph when there is no title.
 * Both states are decorative: the thumbnail always sits beside the SKU or product
 * name that carries the meaning, so the `<img>` is `alt=""` (and the placeholder
 * `aria-hidden`) — this also keeps it out of a table cell's accessible name, so a
 * name-based lookup still matches the text.
 */
export function ProductThumb({
  asin,
  title,
  size = 40,
  className = '',
}: {
  asin: string | null | undefined;
  title?: string | null;
  /** The square's side in pixels. 40 in a table; 120 on the SKU detail header. */
  size?: number;
  className?: string;
}) {
  // Track which ASIN failed rather than a plain boolean, so a component reused
  // for a different row (a table re-sort keys some cells by index) recomputes to
  // "not failed" the moment its ASIN prop changes.
  const [failedAsin, setFailedAsin] = useState<string | null>(null);
  const images = useImageAsins();

  const id = asin?.trim() ?? '';
  // Render the image only for an ASIN the shared set says has one; a tile whose
  // ASIN is absent (or before the set has loaded) draws the placeholder with no
  // request. `failedAsin` still guards the rare case of a file vanishing after
  // the set was built.
  const showImage = id !== '' && images.has(id) && failedAsin !== id;

  const shared = `shrink-0 rounded-md align-middle ring-1 ring-inset ring-line ${className}`;
  const box = { width: size, height: size } as const;

  if (showImage) {
    return (
      <img
        loading="lazy"
        src={`/api/catalog/images/${encodeURIComponent(id)}`}
        // Decorative: the adjacent SKU/product name carries the meaning, and an
        // alt here would otherwise land in a table cell's accessible name.
        alt=""
        aria-hidden
        onError={() => setFailedAsin(id)}
        // A stable hook for the e2e catalog smoke test to tell a resolved image
        // (naturalWidth > 0) from the placeholder it falls back to.
        data-thumb-state="image"
        className={`${shared} inline-block bg-surface object-contain`}
        style={box}
      />
    );
  }

  const label = initials(title);
  return (
    <span
      className={`${shared} inline-flex items-center justify-center bg-sunken text-muted`}
      style={box}
      data-thumb-state="placeholder"
      aria-hidden
    >
      {label ? (
        <span className="font-medium leading-none" style={{ fontSize: Math.round(size * 0.4) }}>
          {label}
        </span>
      ) : (
        <PictureGlyph size={size} />
      )}
    </span>
  );
}

/** The first letters of the first one or two words of a title, uppercased. */
function initials(title: string | null | undefined): string {
  if (!title) return '';
  const letters = title
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0] ?? '')
    .join('');
  return letters.slice(0, 2).toUpperCase();
}

/** A neutral image-placeholder icon, for a product with no title to abbreviate. */
function PictureGlyph({ size }: { size: number }) {
  const s = Math.round(size * 0.5);
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}
